/* One simulation run writes one timeseries CSV per meter, all ending in the
 * same results stamp ("...-timeseries-051126_2100.csv"), so the list used to
 * show a multi-meter run as several rows that renamed and deleted together.
 * This folds those per-meter rows into one row per run.
 *
 * A run is its stamp *and* its period. The sim reserves each stamp with a
 * marker blob, but runs written before that could share a minute; two such
 * runs with different periods are different runs and must stay apart. A row
 * with no readable stamp stays on its own, as before. */

/**
 * @typedef {Object} MeterRow - one entry from extractSimulationsInfo
 * @property {string} fileName - the meter's timeseries CSV
 * @property {string|null} meterName
 * @property {string} startDate
 * @property {string} endDate
 * @property {string|null} dateTimeStamp
 */

/**
 * @param {MeterRow[]} rows - per-meter rows, in display order.
 * @returns {Array<MeterRow & {meters: Array<{meterName: string|null, fileName: string}>}>}
 *   one row per run, in the order each run's first row appeared. `meters` lists
 *   every meter, ordered by name (m1, m2, ..., m10). The run row keeps the
 *   `created` of the row it started from, so it stays where the list's time
 *   sort put it, but takes `fileName` and `meterName` from its first meter by
 *   name: the input is sorted newest first, and a row click should open m1,
 *   not whichever meter the sim happened to write last. A single-meter run is
 *   returned exactly as before.
 */
export function groupMeterRowsIntoRuns(rows) {
  const runs = [];
  const byRun = new Map();

  rows.forEach((row) => {
    const meter = { meterName: row.meterName, fileName: row.fileName };
    if (!row.dateTimeStamp) {
      runs.push({ ...row, meters: [meter] });
      return;
    }
    const runKey = `${row.dateTimeStamp}|${row.startDate}_${row.endDate}`;
    const existing = byRun.get(runKey);
    if (existing) {
      existing.meters.push(meter);
      return;
    }
    const run = { ...row, meters: [meter] };
    byRun.set(runKey, run);
    runs.push(run);
  });

  return runs.map((run) => {
    if (run.meters.length < 2) return run;
    const meters = [...run.meters].sort(byMeterName);
    return {
      ...run,
      meters,
      fileName: meters[0].fileName,
      meterName: meters[0].meterName,
    };
  });
}

/* Natural order, so m2 comes before m10. */
const byMeterName = (a, b) =>
  (a.meterName || "").localeCompare(b.meterName || "", undefined, {
    numeric: true,
  });

/**
 * The meters line under a run's name: "m1", or "m1, m2" for a multi-meter run.
 *
 * @param {{meters?: Array<{meterName: string|null}>, meterName?: string|null}} run
 * @returns {string}
 */
export function runMetersLabel(run) {
  const names = (run.meters || [])
    .map((m) => m.meterName)
    .filter(Boolean);
  if (names.length === 0) return run.meterName || "";
  return names.join(", ");
}
